import React from "react";
import { Ban } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Blocked-members list with its inline error/retry and empty states.
 *
 * Extracted verbatim from src/pages/Settings.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged.
 */
export default function BlockedMembersSection({ blocks, blocksError, reloadBlocks }) {
  const unblock = async (blockedId) => {
    try {
      await api.delete(`/blocks/${blockedId}`);
      toast.success("Member unblocked.");
      reloadBlocks();
    } catch (err) {
      devWarn("Settings.unblock", err);
      toast.error(apiError(err, "We could not unblock this member."));
    }
  };

  return (
    <section className="mt-8 sm-card p-6 bg-white">
      <h2 className="font-display text-xl font-bold text-[#1F2A2E]">Blocked members</h2>
      {blocksError ? (
        <div data-testid="blocks-error" className="mt-3">
          <p className="text-sm text-[#7A2A22]">
            We could not load your blocked members just now.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={reloadBlocks}
            data-testid="blocks-retry-button"
            className="mt-2 bg-white border-[#E3DED4]"
          >
            Try again
          </Button>
        </div>
      ) : blocks.length === 0 ? (
        <p className="mt-2 text-sm text-[#4B5A60]">You have not blocked anyone.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[#E3DED4]">
          {blocks.map((b) => (
            <li key={b.id} className="py-3 flex items-center gap-3">
              <Ban className="w-4 h-4 text-[#B42318]" />
              <span className="text-sm text-[#1F2A2E] flex-1">
                {b.user?.display_name || "Member"}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => unblock(b.blocked_id)}
                className="bg-white border-[#E3DED4]"
              >
                Unblock
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
